/**
 * Why a team-document or folder name cannot be used, or null when it can.
 *
 * A name becomes one segment of a Firebase Storage object path, and Storage has
 * no folders — a "/" is simply a segment separator. So "kvitton/utlägg" did not
 * create one folder with a slash in its name: it created "kvitton" holding
 * "utlägg", while the metadata row kept the whole string as its fileName and so
 * matched nothing the listing showed.
 *
 * - `slash`: any "/", for that reason.
 * - `reserved`: "." and "..", which are path navigation rather than names, and
 *   ".keep", the empty placeholder object written into every folder so an empty
 *   folder still lists — a document of that name would overwrite it and be
 *   hidden from the listing.
 * - `empty`: nothing but whitespace.
 *
 * The name is judged as it will be stored, i.e. trimmed.
 */
export type DocumentNameProblem = 'empty' | 'slash' | 'reserved'

const RESERVED_DOCUMENT_NAMES = new Set(['.', '..', '.keep'])

export function documentNameProblem(name: string): DocumentNameProblem | null {
  const trimmed = name.trim()
  if (trimmed === '') return 'empty'
  if (trimmed.includes('/')) return 'slash'
  if (RESERVED_DOCUMENT_NAMES.has(trimmed)) return 'reserved'
  return null
}
